/**
 * PawLink native (Capacitor) bridge.
 *
 * All exports here are safe to call from a pure web/PWA context — they detect
 * whether the app is running inside a Capacitor native container and become
 * no-ops on the web.
 *
 * Responsibilities:
 *   1. Register the device for FCM push (Android via @capacitor/push-notifications).
 *   2. POST the FCM device token to /api/devices/register with the user's JWT.
 *   3. Handle deep-link app-open events (getpawlink.com/p/<publicId>) by
 *      routing to the in-app rescue page instead of navigating to the web.
 *   4. Route on notification tap using the `deep_link` data payload we send
 *      from the backend.
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

let _initialised = false;

export function isNative() {
    try {
        // @capacitor/core is bundled; the check is cheap and static.
        // We use dynamic import in the function so pure-web builds don't
        // fail if @capacitor/core is tree-shaken.
        // Guarded with `window` to be SSR-safe.
        if (typeof window === "undefined") return false;
        const cap = window.Capacitor;
        return !!(cap && cap.isNativePlatform && cap.isNativePlatform());
    } catch {
        return false;
    }
}

async function _postDeviceToken(token, jwt) {
    if (!BACKEND_URL || !token || !jwt) return;
    try {
        await axios.post(
            `${BACKEND_URL}/api/devices/register`,
            { token, platform: "android" },
            { headers: { Authorization: `Bearer ${jwt}` } },
        );
    } catch (e) {
        // Non-fatal — the user simply won't receive pushes until next launch.
        // eslint-disable-next-line no-console
        console.warn("PawLink: device token registration failed", e?.message || e);
    }
}

/**
 * Initialise all native integrations once, at first authenticated app load.
 *
 * @param {object} opts
 * @param {() => string|null} opts.getJwt  — returns the current JWT (may be null)
 * @param {(path: string) => void} opts.navigate — React Router navigate fn for
 *                                                 in-app routing on deep-link.
 */
export async function initNativeBridge({ getJwt, navigate }) {
    if (_initialised || !isNative()) return;
    _initialised = true;

    // -------- Deep link handler (App Links → open in-app) --------
    try {
        const { App } = await import("@capacitor/app");
        App.addListener("appUrlOpen", (event) => {
            try {
                const url = new URL(event.url);
                // We only handle https://getpawlink.com/p/<publicId>
                if (url.pathname && url.pathname.startsWith("/p/")) {
                    navigate(url.pathname + url.search);
                }
            } catch {
                /* ignore malformed URLs */
            }
        });
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("PawLink: @capacitor/app not available", e?.message);
    }

    // -------- FCM push registration --------
    try {
        const { PushNotifications } = await import("@capacitor/push-notifications");

        // 1) Ask for permission (Android 13+ POST_NOTIFICATIONS runtime prompt).
        let permission = await PushNotifications.checkPermissions();
        if (permission.receive !== "granted") {
            permission = await PushNotifications.requestPermissions();
        }
        if (permission.receive !== "granted") {
            // User denied; nothing more we can do.
            return;
        }

        // 2) Register (fires 'registration' when FCM issues the token).
        PushNotifications.addListener("registration", (tokenResult) => {
            const jwt = getJwt?.();
            _postDeviceToken(tokenResult.value, jwt);
        });

        PushNotifications.addListener("registrationError", (err) => {
            // eslint-disable-next-line no-console
            console.warn("PawLink FCM registration error", err);
        });

        // 3) Deep-link on notification tap — the backend puts `deep_link` and
        //    `public_id` in the FCM data payload.
        PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
            const data = action?.notification?.data || {};
            const publicId = data.public_id;
            if (publicId) {
                navigate(`/p/${publicId}`);
            } else if (data.deep_link) {
                try {
                    const u = new URL(data.deep_link);
                    navigate(u.pathname + u.search);
                } catch {
                    /* ignore */
                }
            }
        });

        await PushNotifications.register();
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("PawLink: push notifications unavailable", e?.message);
    }
}
